import { $api, type InferResponse } from '@/api/typedClient'
import { useTenantStore } from '@/stores/tenantStore'
import type { components } from '@/types/api/v1'
import type { EnvMode } from '@/utils/envMode'

export type OrderDashboardReport = InferResponse<
  '/crossroads/dashboard/orders/metrics',
  'post'
>
export type PartnerMetric = components['schemas']['PartnerMetric']
export type CheckpointMetric = components['schemas']['CheckpointMetric']

/**
 * Order integration metrics for a single window, broken out by partner tenant
 * name -- both `partner_metrics` and `checkpoint_metrics` are keyed by it, so
 * picking a partner narrows the event grid as well as the issue list. The
 * backend caps the window at 32 days, which the range picker stays inside.
 */
export function useOrderDashboardReport(
  reportWindow: { start: string; end: string },
  /** Test/Prod, resolved server-side against the tenant's own instances.
   * Undefined means every instance. */
  mode?: EnvMode,
  enabled: boolean = true,
) {
  const { selectedTenant } = useTenantStore()

  return $api.useQuery(
    'post',
    '/crossroads/dashboard/orders/metrics',
    {
      body: {
        start_date: reportWindow.start,
        end_date: reportWindow.end,
        mode,
        // The issues endpoint shares this request model; its filters mean
        // nothing to the metrics report but are required on the wire.
        warnings: false,
        my_issues: false,
        partner_issues: false,
      },
    },
    {
      enabled: enabled && !!selectedTenant?.name,
      staleTime: 5 * 60 * 1000,
    },
  )
}

export type OrderDashboardMappingMetrics = InferResponse<
  '/crossroads/dashboard/orders/mappings',
  'post'
>
export type MappingCount = components['schemas']['MappingCount']

/**
 * Mapping coverage by reference-data type. Not scoped to a window -- coverage
 * is a snapshot of how much of the tenant's reference data is linked up right
 * now, so it is unaffected by the dashboard's range picker. It also barely
 * moves day to day, hence the long staleTime.
 */
export function useOrderDashboardMappingMetrics(enabled: boolean = true) {
  const { selectedTenant } = useTenantStore()

  return $api.useQuery(
    'post',
    '/crossroads/dashboard/orders/mappings',
    {},
    {
      enabled: enabled && !!selectedTenant?.name,
      staleTime: 24 * 60 * 60 * 1000,
    },
  )
}

export type OrderDashboardIssue = InferResponse<
  '/crossroads/dashboard/orders/issues',
  'post'
>[number]

/**
 * Every unresolved failure on orders in the window. The request's own
 * severity/category/ownership filters are deliberately left wide open -- the
 * section filters what comes back on the frontend, so switching scope or
 * severity card costs no round trip.
 */
export function useOrderDashboardIssues(
  reportWindow: { start: string; end: string },
  /** Test/Prod, resolved server-side against the tenant's own instances.
   * Undefined means every instance. */
  mode?: EnvMode,
  enabled: boolean = true,
) {
  const { selectedTenant } = useTenantStore()

  return $api.useQuery(
    'post',
    '/crossroads/dashboard/orders/issues',
    {
      body: {
        start_date: reportWindow.start,
        end_date: reportWindow.end,
        mode,
        // Warnings are the one thing the section does not show (see
        // ISSUE_BUCKET_KEYS), so they are left out of the payload rather than
        // fetched and dropped. The ownership flags stay false because
        // ownership is filtered on the frontend.
        warnings: false,
        my_issues: false,
        partner_issues: false,
      },
    },
    {
      enabled: enabled && !!selectedTenant?.name,
      staleTime: 5 * 60 * 1000,
    },
  )
}
